<template>
  <div class="modal fade" id="deleteUserModal" ref="deleteUserModal" tabindex="-1" role="dialog" aria-hidden="true" data-bs-backdrop="true">
      <div class="modal-dialog modal-lg" role="document">
          <div class="modal-content" v-if="selectedUser">
              <div class="modal-header">
                  <h5 class="modal-title">Delete User &quot;<span class="text-primary">{{ selectedUser.name }}</span>&quot;</h5>
                  <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body">
                <p>{{ $t('page.manage-users.deleteUserConfirmation') }}</p>
                <UserItem :user="selectedUser" />
              </div>
              <div class="modal-footer">
                  <button type="submit" class="btn btn-danger" @click="delete_user(selectedUser.id)">
                    <font-awesome-layers class="fa-1x me-2">
                      <font-awesome-icon :icon="['fas', 'trash']" />
                    </font-awesome-layers>
                    <span>{{ $t('page.manage-users.deleteUser') }}</span>
                  </button>
                  <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">
                    <font-awesome-layers class="fa-1x me-2">
                      <font-awesome-icon :icon="['fas', 'xmark']" />
                    </font-awesome-layers>
                    {{ $t('action.cancel') }}
                  </button>
              </div>
          </div>
      </div>
  </div>
  <h2 class="text-primary mx-2">{{ $t('page.manage-users.form.title') }}</h2>
  <div class="container-fluid" v-if="isSystemAdmin || isTenantAdmin || isSiteAdmin">

    <div id="manage-users-row" class="row">
      <div class="col-4 overflow-y-auto" :style="scrollableColumnStyle">
        <div class="mel-divider d-flex align-items-center my-3">
          <p class="text-center fw-bold mx-3 mb-0 text-primary">{{ $t('page.manage-users.usersList') }}</p>
        </div>
        <div class="input-group input-group-sm mb-2">
          <span class="input-group-text">
            <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
          </span>
          <input type="search" class="form-control" v-model="searchTerm" :placeholder="$t('action.search')" />
        </div>
        <!-- existing users list -->
        <ul class="nav nav-pills flex-column">
          <li v-if="isSystemAdmin || isTenantAdmin">
            <a @click="select_user(0)" href="#" class="nav-link text-decoration-none fst-italic"
            :class="{ 'active bg-melodi text-light': selectedUserId === 0, 'text-melodi': selectedUserId !== 0 }">
            <div>
              <span class="d-none d-xl-inline">
                <font-awesome-layers class="fa-1x me-2">
                  <font-awesome-icon :icon="['fas', 'user-plus']" />
                </font-awesome-layers>
                <span class="fst-italic">{{ $t('page.manage-users.newUserEllipsis') }}</span>
              </span>
            </div>
            </a>
          </li>
          <li v-for="user in sortedUsers" :key="user.id">
            <a @click="select_user(user.id)" href="#" class="nav-link text-decoration-none"
            :class="{ 'active': selectedUserId === user.id }">
              <UserItem :user="user" />
            </a>
          </li>
        </ul>
      </div>
      <div class="col-4">
        <div v-if="selectedUserId === 0 || !selectedUser">
          <!-- new user form -->
          <div class="mel-divider d-flex align-items-center my-3">
            <p class="text-center fw-bold mx-3 mb-0 text-primary">{{ $t('page.manage-users.newUserTitle') }}</p>
          </div>
          <Form @submit="onSubmitUserInvite" :validation-schema="schema" v-slot="{ errors, isSubmitting }" class="m-2" :disabled="assignedRoles.length === 0">
            <div class="mb-3">
              <div class="form-floating">
                <Field id="email" name="email" type="email" class="form-control" placeholder="name@example.com" :class="{ 'is-invalid': errors.email }" />
                <label for="email">{{ $t('field.generic.email') }}</label>
                <div class="invalid-feedback" v-if="!!errors.code">{{ errors.code }}</div>
              </div>
            </div>
            <div class="d-flex justify-content-between">
              <button type="submit" class="btn btn-primary me-auto" :disabled="isSubmitting || assignedRoles.length === 0">
                <font-awesome-layers class="fa-1x me-2">
                  <font-awesome-icon :icon="['fas', 'user-plus']" />
                </font-awesome-layers>
                <span>{{ $t('page.manage-users.form.inviteUser') }}</span>
              </button>
            </div>
            <div v-if="errors.apiError" class="alert alert-danger mt-3 mb-0">{{ errors.apiError }}</div>
          </Form>
        </div>
        <div v-else>
          <!-- selected user controls -->
          <div class="mel-divider d-flex align-items-center my-3">
            <p class="text-center fw-bold mx-3 mb-0 text-primary">{{ $t('page.manage-users.user') }} - {{ selectedUser.name }}</p>
          </div>
          <Form @submit="onSubmitUserChange" :validation-schema="schema" v-slot="{ errors, isSubmitting }" class="m-2" :disabled="assignedRoles.length === 0">
            <Field name="userId" type="hidden" v-model="selectedUser.id" />
            <div class="form-floating mb-3">
              <Field id="email" name="email" type="email" class="form-control" placeholder="name@example.com" :class="{ 'is-invalid': errors.email }" v-model="selectedUser.email" :disabled="!isSystemAdmin" />
              <label for="email">{{ $t('field.generic.email') }}</label>
              <div class="invalid-feedback" v-if="!!errors.code">{{ errors.code }}</div>
            </div>
            <div class="form-floating mb-3">
              <Field id="fullName" name="fullName" type="text" class="form-control" :placeholder="$t('field.user.fullNamePlaceholder')" maxlength="40" :class="{ 'is-invalid': errors.fullName }" v-model="selectedUser.name" />
              <label for="fullName">{{ $t('field.user.fullName') }}</label>
              <div class="invalid-feedback"><ErrorMessage name="fullName" /></div>
            </div>
            <div class="btn-toolbar justify-content-between" role="group">
              <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
                <font-awesome-layers class="fa-1x me-2">
                  <font-awesome-icon :icon="['fas', 'floppy-disk']" />
                </font-awesome-layers>
                <span>{{ $t('action.save') }}</span>
              </button>
              <button v-if="!isSelf" type="button" class="btn btn-outline-danger" data-bs-toggle="modal" data-bs-target="#deleteUserModal">
                <font-awesome-layers class="fa-1x me-2">
                  <font-awesome-icon :icon="['fas', 'trash']" />
                </font-awesome-layers>
                <span>{{ $t('page.manage-users.deleteUser') }}&hellip;</span>
              </button>
            </div>
          </Form>
        </div>
        <div class="mel-divider mel-divider-success d-flex align-items-center my-3">
          <p class="text-center fw-bold mx-3 mb-0 text-primary">{{ $t('global.assignedRoles', assignedRoles.length) }}</p>
        </div>
        <!-- selected roles for existing user / invitation -->
        <div v-if="sortedAssignedRoles.length === 0" class="alert alert-melodi" role="alert">{{ $t('page.manage-users.selectAtLeastOneRole') }}</div>
        <ul class="nav nav-pills flex-column">
          <li v-for="selectedRole in sortedAssignedRoles" :key="(selectedRole.code, selectedRole.tenant?.id, selectedRole.site?.id)">
            <a @click="role_toggle(selectedRole.code, selectedRole.displayName, selectedRole.tenant, selectedRole.site)" href="#" class="nav-link text-decoration-none" :class="{ 'disabled': isRolesLoading }">
              <span class="ms-1 text" v-if="!!selectedRole.tenant && !!selectedRole.site">{{ selectedRole.tenant.name }} - {{ selectedRole.site.name }}: </span>
              <span class="ms-1 text" v-else-if="!!selectedRole.tenant && !selectedRole.site">{{ selectedRole.tenant.name }}: </span>
              <span class="ms-1 text fw-bold">{{ selectedRole.displayName }}</span>
            </a>
          </li>
        </ul>
      </div>

      <div class="col-4 overflow-y-auto bg-body-tertiary border border-secondary-subtle border-end-0 rounded-start pb-3" :style="scrollableColumnStyle">
        <!-- selectable tenants -->
        <div class="mel-divider d-flex align-items-center my-3">
          <p class="text-center fw-bold mx-3 mb-0 text-primary">{{ $t('global.tenants', userTenants.length) }}</p>
        </div>
        <ul class="nav nav-pills flex-column">
          <li v-if="isSystemAdmin">
            <a @click="select_tenant(0)" href="#" class="nav-link text-decoration-none fst-italic"
              :class="{ 'active bg-melodi text-light': selectedTenantId === 0, 'text-melodi': selectedTenantId !== 0 }">
              <font-awesome-layers class="fa-1x me-2">
                <font-awesome-icon :icon="['fas', 'building']" />
              </font-awesome-layers>
              <span class="ms-1 text">{{ $t('global.any' )}}</span>
            </a>
          </li>
          <li v-for="tenant in sortedTenants" :key="tenant.id">
            <a @click="select_tenant(tenant.id)" href="#" class="nav-link text-decoration-none"
              :class="{ 'active': selectedTenantId === tenant.id }">
              <font-awesome-layers class="fa-1x me-2">
                <font-awesome-icon :icon="['fas', 'building']" />
              </font-awesome-layers>
              <span class="ms-1 text">{{ tenant.name }}</span>
            </a>
          </li>
        </ul>

        <!-- selectable sites (dependent on tenant selection) -->
        <div v-if="selectedTenantId !== 0">
          <div class="mel-divider mel-divider-success d-flex align-items-center my-3">
            <p class="text-center fw-bold mx-3 mb-0 text-primary">{{ $t('global.sites', tenantFilteredUserSites.length) }}</p>
          </div>
          <ul class="nav nav-pills flex-column">
            <li v-if="isSystemAdmin || isTenantAdmin">
              <a @click="select_site(0)" href="#" class="nav-link text-decoration-none fst-italic"
              :class="{ 'active bg-melodi text-light': selectedSiteId === 0, 'text-melodi': selectedSiteId !== 0 }">
                <font-awesome-layers class="fa-1x me-2">
                  <font-awesome-icon :icon="['fas', 'warehouse']" />
                </font-awesome-layers>
                <span class="ms-1 text">{{ $t('global.any' )}}</span>
              </a>
            </li>
            <li v-for="site in tenantFilteredUserSites" :key="site.id">
              <a @click="select_site(site.id)" href="#" class="nav-link text-decoration-none"
                :class="{ 'active': selectedSiteId === site.id }">
                <font-awesome-layers class="fa-1x me-2">
                  <font-awesome-icon :icon="['fas', 'warehouse']" />
                </font-awesome-layers>
                <span class="ms-1 text">{{ site.name }}</span>
              </a>
            </li>
          </ul>
        </div>

        <!-- selectable roles (dependent on tenant and site selection) -->
        <div class="mel-divider mel-divider-success d-flex align-items-center my-3">
          <p class="text-center fw-bold mx-3 mb-0 text-primary">{{ $t('global.role', 0) }}</p>
        </div>
        <ul v-if="isSystemAdmin || isTenantAdmin || isSiteAdmin" class="nav nav-pills flex-column">
          <li v-for="relatedRole in relatedRoles" :key="relatedRole.code">
            <a @click="role_toggle(relatedRole.code, relatedRole.displayName, selectedTenant, selectedSite)" href="#" class="nav-link text-decoration-none" :class="{ 'disabled': isRolesLoading }">
              <input type="checkbox" :checked="relatedRole.isSelected" class="form-check-input" />
              <span class="ms-1 text">{{ relatedRole.displayName }}</span>
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>
  <div class="container-fluid" v-else>
    <div class="alert alert-danger" role="alert">
      <h4 class="alert-heading">
        <font-awesome-layers class="fa-1x me-2">
          <font-awesome-icon :icon="['fas', 'shield']" />
        </font-awesome-layers>
        {{ $t('error.pageAccessDenied') }}
      </h4>
      <span>{{ $t('validation.requiredPermissions', { 'permissions': 'System Admin, Tenant Admin, Site Admin' }) }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { toast } from 'vue3-toastify';
import * as Yup from 'yup';
import { Form, Field, GenericObject, ErrorMessage } from 'vee-validate';
import { Modal } from 'bootstrap';
import { compareOptionalString } from "@/services";
import { useSessionStore, useIdentityManagementStore, useUsersStore } from '@/stores';
import InviteUserApiRequest from '@/models/requests/InviteUserApiRequest';
import AdminUpdateUserApiRequest from '@/models/requests/AdminUpdateUserApiRequest';
import AccessControlEntryRequest from '@/models/requests/AccessControlEntryRequest';
import { RoleDescriptions } from "@/models/RoleCodes";
import RoleSelect from "@/models/frontend/RoleSelect";
import Tenant from "@/models/frontend/Tenant";
import Site from "@/models/frontend/Site";
import RoleCheckedDisplay from '@/models/frontend/roleCheckedDisplay';
import UserItem from '@/components/UserItem.vue';
import RoleDisplay from '@/models/frontend/roleDisplay';
import User from '@/models/frontend/User';

const i18n = useI18n();

const sessionStore = useSessionStore();
const { user: sessionUser, userTenants, userSites, selectedTenantId, selectedSiteId, selectedTenant, selectedSite, isSystemAdmin, isTenantAdmin, isSiteAdmin } = storeToRefs(sessionStore);
const usersStore = useUsersStore();
const { users, userRoles } = storeToRefs(usersStore);

const selectedUserId = ref(0);
const selectedUser = ref(null as User | null);
const searchTerm = ref('');
const isSelf = computed(() => !!selectedUser.value && selectedUser.value.id === sessionUser.value?.id);

const isRolesLoading = ref(false);

const deleteUserModal = ref();

const offsetTop = ref(0);
const offsetTopInterval = ref<NodeJS.Timeout | null>(null);

onMounted(() => {
  // workaround for the offsetTop calculation, as the component might not be fully rendered when the offsetTop is calculated
  offsetTopInterval.value = setInterval(() => {
    offsetTop.value = document.querySelector('#manage-users-row')?.getBoundingClientRect().top ?? 0;
  }, 1000);
  offsetTop.value = document.querySelector('#manage-users-row')?.getBoundingClientRect().top ?? 0;
});

onUnmounted(() => {
  if (offsetTopInterval.value) {
    clearInterval(offsetTopInterval.value);
    offsetTopInterval.value = null;
  }
});

const scrollableColumnStyle = computed(() => ({
  height: `calc(100vh - ${offsetTop.value}px)`,
}));

const hideModal = () => {
  const modalElement = deleteUserModal.value as HTMLElement;
  const modal = Modal.getInstance(modalElement);
  modal?.hide();
  const backdrop = document.querySelector('.modal-backdrop');
  if (backdrop) {
    backdrop.remove();
  }
};

const sortedTenants = computed(() => {
  return [...userTenants.value].sort((a, b) => a.name.localeCompare(b.name));
});

const sortedUsers = computed(() => {
  const term = searchTerm.value.trim().toLowerCase();
  return [...users.value]
    .filter(u => !u.isSystem)
    .filter(u => !term
      || (u.name?.toLowerCase().includes(term) ?? false)
      || u.email.toLowerCase().includes(term))
    .sort((a, b) =>
      compareOptionalString(a.name, b.name) ||
      a.email.localeCompare(b.email));
});

const assignedRoles = ref([] as RoleSelect[]);
const sortedAssignedRoles = computed(() => {
  return [...assignedRoles.value].sort((a, b) =>
    compareOptionalString(a.tenant?.name, b.tenant?.name) ||
    compareOptionalString(a.site?.name, b.site?.name) ||
    compareOptionalString(a.displayName, b.displayName));
});

const tenantFilteredUserSites = computed(() => {
  if (selectedTenantId.value === 0) return userSites.value;
  return userSites.value
    ?.filter((site) => site.tenant.id === selectedTenantId.value)
    .sort((a, b) => a.name.localeCompare(b.name));
});

const select_tenant = async (id: number) => {
  await sessionStore.selectTenant(id);
};
const select_site = async (id: number) => {
  await sessionStore.selectSite(id);
};
const select_user = async (id: number) => {
  selectedUserId.value = id;
  selectedUser.value = usersStore.getUserById(selectedUserId.value);
  if (id === 0)
  {
    assignedRoles.value = [];
    relatedRoles.value.forEach(rd => rd.isSelected = false);
    return;
  }

  isRolesLoading.value = true;

  await usersStore.getRoles(id);

  // use the user roles to populate the selected roles
  const selectedUserRoles = userRoles.value;
  assignedRoles.value = selectedUserRoles.map(ur => new RoleSelect(ur.code, ur.displayName, ur.tenant ?? ur.site?.tenant, ur.site));

  // update the related roles to reflect the selected user roles
  relatedRoles.value.forEach(rd => {
    rd.isSelected = selectedUserRoles.some(ur =>
      ur.code === rd.code
      && (ur.tenant == undefined || ur.tenant.id === selectedTenantId.value)
      && (ur.site == undefined || ur.site.id === selectedSiteId.value)
    );
  });

  isRolesLoading.value = false;
};
const delete_user = (id: number) => {
  hideModal();
  usersStore.deleteUser(id);
  selectedUserId.value = 0;
  selectedUser.value = null;
};

const relatedRoles = computed(() => {
  let roleDisplays: RoleDisplay[] = [];
  if (selectedTenantId.value === 0) {
    roleDisplays = RoleDescriptions.filter(rd => rd.code.startsWith('SYS'));
  }
  else if (selectedSiteId.value === 0) {
    roleDisplays = RoleDescriptions.filter(rd => rd.code.startsWith('TEN'));
  }
  else {
    roleDisplays = RoleDescriptions.filter(rd => rd.code.startsWith('SIT'));
  }

  return roleDisplays.map(rd => new RoleCheckedDisplay(rd.code, rd.displayName, isRoleSelected(rd.code)));
});

const isRoleSelected = (roleCode: string) => {
  return assignedRoles.value.some(rs =>
    rs.code === roleCode
    && (rs.tenant?.id ?? 0) === selectedTenantId.value
    && (rs.site?.id ?? 0) === selectedSiteId.value);
};

const schema = Yup.object().shape({
  email: Yup.string().email(i18n.t('validation.emailRequired')).required(i18n.t('validation.emailRequired')).max(256, i18n.t('validation.maxLength', { max: 256 })),
});

const role_toggle = (roleCode: string, displayName: string, tenant?: Tenant, site?: Site) => {
  const index = assignedRoles.value.findIndex(rs =>
    rs.code === roleCode &&
    (rs.tenant?.id ?? 0) === (tenant?.id ?? 0) &&
    (rs.site?.id ?? 0) === (site?.id ?? 0)
  );
  var relatedRole = relatedRoles.value.find(rd => rd.code === roleCode);
  if (index === -1) {
    assignedRoles.value.push(new RoleSelect(roleCode, displayName, tenant, site));
    if (relatedRole) {
      relatedRole.isSelected = true;
    }
  }
  else {
    assignedRoles.value.splice(index, 1);
    if (relatedRole) {
      relatedRole.isSelected = false;
    }
  }
};

async function onSubmitUserInvite(values: GenericObject, { setErrors }: GenericObject) {
  const { email } = values;

  const identityManagementStore = useIdentityManagementStore();

  try {
    const aces = assignedRoles.value.map((role) => new AccessControlEntryRequest(role.code, role.tenant?.id, role.site?.id));
    const requestBody = new InviteUserApiRequest(email, aces);
    await identityManagementStore.inviteUser(requestBody);
    toast.success(i18n.t('page.manage-users.inviteUserSuccess'), { autoClose: 3000 });
  } catch (error) {
    return setErrors({ apiError: error });
  }
}

async function onSubmitUserChange(values: GenericObject, { setErrors }: GenericObject) {
  const { userId, email, fullName } = values;

  const identityManagementStore = useIdentityManagementStore();

  try {
    const aces = assignedRoles.value.map((role) => new AccessControlEntryRequest(role.code, role.tenant?.id, role.site?.id));
    const requestBody = new AdminUpdateUserApiRequest(userId, email, fullName, aces);
    await identityManagementStore.adminUpdateUser(requestBody);
    toast.success(i18n.t('page.manage-users.userChangSaveSuccess'), { autoClose: 3000 });
  } catch (error) {
    return setErrors({ apiError: error });
  }
}
</script>
